import { useEffect, useMemo, useState } from "react";

// @mui
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";

// Material Dashboard Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// API helper
import { apiFetch } from "utils/api";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

const emptyCreate = {
  customerNumber: "",
  companyName: "",
  contactName: "",
  emailLogin: "",
  password: "",
  street: "",
  zip: "",
  city: "",
  country: "Deutschland",
  vatId: "",
  accountHolder: "",
  iban: "",
  bic: "",
};

export default function AdminCustomers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [createBusy, setCreateBusy] = useState(false);

  // Manage
  const [manageOpen, setManageOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [manageForm, setManageForm] = useState(null);
  const [manageBusy, setManageBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [accessBusyId, setAccessBusyId] = useState(null);
  const [accessBusyTargetBlocked, setAccessBusyTargetBlocked] = useState(null);

  const canCreate = useMemo(() => {
    return (
      createForm.customerNumber.trim() &&
      createForm.companyName.trim() &&
      createForm.emailLogin.trim() &&
      createForm.password.trim()
    );
  }, [createForm]);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("de-DE");
  };

  const loadCustomers = async () => {
    setLoading(true);
    setErr("");

    try {
      const data = await apiFetch("/api/admin/customers");
      // backend may return array or { customers: [...] }
      const list = Array.isArray(data) ? data : data?.customers || [];
      setRows(list);
    } catch (e) {
      setErr(e?.message || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openManage = (customer) => {
    setActiveCustomer(customer);
    setManageForm({
      id: customer.id || "",
      customerNumber: customer.customerNumber || "",
      companyName: customer.companyName || "",
      contactName: customer.contactName || "",
      emailLogin: customer.emailLogin || "",
      street: customer.street || "",
      zip: customer.zip || "",
      city: customer.city || "",
      country: customer.country || "Deutschland",
      vatId: customer.vatId || "",
      paymentType: customer.paymentType || "Lastschrift",
      accountHolder: customer.accountHolder || "",
      iban: customer.iban || "",
      bic: customer.bic || "",
      accessBlocked: !!customer.accessBlocked,
      accessBlockedReason: customer.accessBlockedReason || "",
      accessBlockedAt: customer.accessBlockedAt || null,
    });
    setManageOpen(true);
  };

  const closeManage = () => {
    setManageOpen(false);
    setActiveCustomer(null);
    setManageForm(null);
    setManageBusy(false);
    setDeleteBusy(false);
  };

  const submitCreate = async () => {
    setCreateBusy(true);
    setErr("");

    try {
      // Create is an admin action (backend: /api/admin/customers)
      await apiFetch("/api/admin/customers", {
        method: "POST",
        body: {
          customerNumber: createForm.customerNumber.trim(),
          companyName: createForm.companyName.trim(),
          contactName: createForm.contactName.trim() || null,
          emailLogin: createForm.emailLogin.trim(),
          password: createForm.password,
          street: createForm.street.trim() || null,
          zip: createForm.zip.trim() || null,
          city: createForm.city.trim() || null,
          country: createForm.country.trim() || "Deutschland",
          vatId: createForm.vatId.trim() || null,
          paymentType: "Lastschrift",
          accountHolder: createForm.accountHolder.trim() || null,
          iban: createForm.iban.trim() || null,
          bic: createForm.bic.trim() || null,
        },
      });

      setCreateOpen(false);
      setCreateForm(emptyCreate);
      await loadCustomers();
    } catch (e) {
      setErr(e?.message || "Kunde konnte nicht angelegt werden");
    } finally {
      setCreateBusy(false);
    }
  };

  const submitUpdate = async () => {
    if (!activeCustomer) return;
    setManageBusy(true);
    setErr("");

    try {
      const key = activeCustomer?.id;
      if (!key) throw new Error("Customer-ID fehlt (activeCustomer.id). Bitte Seite neu laden.");
      await apiFetch(`/api/admin/customers/${encodeURIComponent(key)}`, {
        method: "PUT",
        body: {
          // keep customerNumber immutable here (admin can change later if needed)
          companyName: manageForm.companyName.trim(),
          contactName: manageForm.contactName.trim() || null,
          emailLogin: manageForm.emailLogin.trim() || null,

          // Billing address
          street: (manageForm.street || "").trim() || null,
          zip: (manageForm.zip || "").trim() || null,
          city: (manageForm.city || "").trim() || null,
          country: (manageForm.country || "").trim() || "Deutschland",
          vatId: (manageForm.vatId || "").trim() || null,

          // Payment (Lastschrift)
          paymentType: "Lastschrift",
          accountHolder: (manageForm.accountHolder || "").trim() || null,
          iban: (manageForm.iban || "").trim() || null,
          bic: (manageForm.bic || "").trim() || null,
        },
      });

      closeManage();
      await loadCustomers();
    } catch (e) {
      setErr(e?.message || "Kunde konnte nicht gespeichert werden");
    } finally {
      setManageBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!activeCustomer) return;

    // quick confirm (simple + effective)
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Kunde ${activeCustomer.customerNumber} wirklich löschen?\n\nAchtung: Screens, Medien & Rechnungen werden ebenfalls gelöscht.`
    );
    if (!ok) return;

    setDeleteBusy(true);
    setErr("");

    try {
      const key = activeCustomer?.id;
      if (!key) throw new Error("Customer-ID fehlt (activeCustomer.id). Bitte Seite neu laden.");

      await apiFetch(`/api/admin/customers/${encodeURIComponent(key)}`, { method: "DELETE" });

      closeManage();
      await loadCustomers();
    } catch (e) {
      setErr(e?.message || "Kunde konnte nicht gelöscht werden");
    } finally {
      setDeleteBusy(false);
    }
  };

  const applyCustomerUpdateLocal = (updatedCustomer) => {
    if (!updatedCustomer?.id) return;

    setRows((prev) => prev.map((item) => (item.id === updatedCustomer.id ? updatedCustomer : item)));

    setActiveCustomer((prev) => (prev?.id === updatedCustomer.id ? updatedCustomer : prev));

    setManageForm((prev) => {
      if (!prev || prev.id !== updatedCustomer.id) return prev;
      return {
        ...prev,
        accessBlocked: !!updatedCustomer.accessBlocked,
        accessBlockedReason: updatedCustomer.accessBlockedReason || "",
        accessBlockedAt: updatedCustomer.accessBlockedAt || null,
      };
    });
  };

  const toggleCustomerAccess = async (customer, blocked) => {
    const key = customer?.id;
    if (!key) return;

    const actionLabel = blocked ? "sperren" : "entsperren";
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Zugang für ${customer.companyName} wirklich ${actionLabel}?`);
    if (!ok) return;

    setAccessBusyId(key);
    setAccessBusyTargetBlocked(blocked);
    setErr("");

    try {
      const updated = await apiFetch(`/api/admin/customers/${encodeURIComponent(key)}/access`, {
        method: "PATCH",
        body: {
          blocked,
          reason: blocked ? "Zugang wurde vom Admin gesperrt" : null,
        },
      });
      applyCustomerUpdateLocal(updated);
    } catch (e) {
      setErr(e?.message || "Zugang konnte nicht geändert werden");
    } finally {
      setAccessBusyId(null);
      setAccessBusyTargetBlocked(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h5" fontWeight="medium">
          Kunden
        </MDTypography>

        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => setCreateOpen(true)}
        >
          Neuer Kunde
        </MDButton>
      </MDBox>

      {!!err && (
        <MDBox mb={2}>
          <MDTypography variant="button" color="error">
            {err}
          </MDTypography>
        </MDBox>
      )}

      <Card>
        <MDBox p={2}>
          {loading ? (
            <MDTypography variant="button" color="text">
              Lädt…
            </MDTypography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Kundennummer</TableCell>
                    <TableCell>Firma</TableCell>
                    <TableCell>Ansprechpartner</TableCell>
                    <TableCell>Login E-Mail</TableCell>
                    <TableCell>Zugang</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.id ?? c.customerNumber} hover>
                      <TableCell>{c.customerNumber}</TableCell>
                      <TableCell>
                        <MDButton
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ p: 0, minWidth: 0, textTransform: "none" }}
                          onClick={() => openManage(c)}
                        >
                          {c.companyName}
                        </MDButton>
                      </TableCell>
                      <TableCell>{c.contactName || "-"}</TableCell>
                      <TableCell>{c.emailLogin || "-"}</TableCell>
                      <TableCell>
                        <MDTypography
                          variant="button"
                          color={c.accessBlocked ? "error" : "success"}
                          fontWeight="medium"
                        >
                          {c.accessBlocked ? "Gesperrt" : "Aktiv"}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="right">
                        <MDButton
                          variant="outlined"
                          color={c.accessBlocked ? "success" : "error"}
                          size="small"
                          disabled={accessBusyId === c.id || manageBusy || deleteBusy}
                          onClick={() => toggleCustomerAccess(c, !c.accessBlocked)}
                        >
                          {accessBusyId === c.id
                            ? accessBusyTargetBlocked
                              ? "Sperrt…"
                              : "Entsperrt…"
                            : c.accessBlocked
                              ? "Entsperren"
                              : "Sperren"}
                        </MDButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>Keine Kunden gefunden.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MDBox>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={createOpen} onClose={() => (createBusy ? null : setCreateOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Kunden anlegen</DialogTitle>
        <DialogContent dividers>
          <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
            <TextField
              label="Kundennummer"
              value={createForm.customerNumber}
              onChange={(e) => setCreateForm((p) => ({ ...p, customerNumber: e.target.value }))}
              placeholder="z.B. K-0003"
              fullWidth
            />
            <TextField
              label="Firma"
              value={createForm.companyName}
              onChange={(e) => setCreateForm((p) => ({ ...p, companyName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Ansprechpartner"
              value={createForm.contactName}
              onChange={(e) => setCreateForm((p) => ({ ...p, contactName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Login E-Mail"
              value={createForm.emailLogin}
              onChange={(e) => setCreateForm((p) => ({ ...p, emailLogin: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Initiales Passwort"
              value={createForm.password}
              onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
              type="password"
              fullWidth
            />
          </MDBox>

          <Divider sx={{ my: 2 }} />

          <MDTypography variant="button" color="text" fontWeight="medium">
            Rechnungsadresse
          </MDTypography>
          <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
            <TextField
              label="Straße"
              value={createForm.street}
              onChange={(e) => setCreateForm((p) => ({ ...p, street: e.target.value }))}
              fullWidth
            />
            <TextField
              label="PLZ"
              value={createForm.zip}
              onChange={(e) => setCreateForm((p) => ({ ...p, zip: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Stadt"
              value={createForm.city}
              onChange={(e) => setCreateForm((p) => ({ ...p, city: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Land"
              value={createForm.country}
              onChange={(e) => setCreateForm((p) => ({ ...p, country: e.target.value }))}
              fullWidth
            />
            <TextField
              label="USt-IdNr."
              value={createForm.vatId}
              onChange={(e) => setCreateForm((p) => ({ ...p, vatId: e.target.value }))}
              fullWidth
            />
          </MDBox>

          <Divider sx={{ my: 2 }} />

          <MDTypography variant="button" color="text" fontWeight="medium">
            Zahlungsdaten (Lastschrift)
          </MDTypography>
          <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
            <TextField
              label="Kontoinhaber"
              value={createForm.accountHolder}
              onChange={(e) => setCreateForm((p) => ({ ...p, accountHolder: e.target.value }))}
              fullWidth
            />
            <TextField
              label="IBAN"
              value={createForm.iban}
              onChange={(e) => setCreateForm((p) => ({ ...p, iban: e.target.value }))}
              fullWidth
            />
            <TextField
              label="BIC"
              value={createForm.bic}
              onChange={(e) => setCreateForm((p) => ({ ...p, bic: e.target.value }))}
              fullWidth
            />
          </MDBox>

          <MDBox mt={2}>
            <MDTypography variant="caption" color="text">
              Hinweis: Du kannst später in „Kunde verwalten“ die Daten ändern. Passwort-Reset bauen wir als nächsten Schritt.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setCreateOpen(false)} disabled={createBusy}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={submitCreate} disabled={!canCreate || createBusy}>
            {createBusy ? "Speichert…" : "Anlegen"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* MANAGE DIALOG */}
      <Dialog open={manageOpen} onClose={closeManage} maxWidth="sm" fullWidth>
        <DialogTitle>Kunde verwalten</DialogTitle>
        <DialogContent dividers>
          {!manageForm ? (
            <MDTypography variant="button" color="text">
              Lädt…
            </MDTypography>
          ) : (
            <>
              <MDBox mb={1}>
                <MDTypography variant="caption" color="text">
                  Kundennummer
                </MDTypography>
                <MDTypography variant="h6" fontWeight="medium">
                  {manageForm.customerNumber}
                </MDTypography>
              </MDBox>

              <MDBox mb={1}>
                <MDTypography variant="caption" color="text">
                  Zugangsstatus
                </MDTypography>
                <MDTypography
                  variant="button"
                  color={manageForm.accessBlocked ? "error" : "success"}
                  fontWeight="medium"
                  display="block"
                >
                  {manageForm.accessBlocked ? "Gesperrt" : "Aktiv"}
                </MDTypography>
                {manageForm.accessBlocked ? (
                  <MDTypography variant="caption" color="text">
                    Seit: {formatDateTime(manageForm.accessBlockedAt)}
                    {manageForm.accessBlockedReason ? ` • ${manageForm.accessBlockedReason}` : ""}
                  </MDTypography>
                ) : null}
                <MDBox mt={1}>
                  <MDButton
                    variant="outlined"
                    color={manageForm.accessBlocked ? "success" : "error"}
                    size="small"
                    disabled={accessBusyId === activeCustomer?.id || manageBusy || deleteBusy}
                    onClick={() => toggleCustomerAccess(activeCustomer, !manageForm.accessBlocked)}
                  >
                    {accessBusyId === activeCustomer?.id
                      ? accessBusyTargetBlocked
                        ? "Sperrt…"
                        : "Entsperrt…"
                      : manageForm.accessBlocked
                        ? "Jetzt entsperren"
                        : "Jetzt sperren"}
                  </MDButton>
                </MDBox>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                <TextField
                  label="Firma"
                  value={manageForm.companyName}
                  onChange={(e) => setManageForm((p) => ({ ...p, companyName: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Ansprechpartner"
                  value={manageForm.contactName}
                  onChange={(e) => setManageForm((p) => ({ ...p, contactName: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Login E-Mail"
                  value={manageForm.emailLogin}
                  onChange={(e) => setManageForm((p) => ({ ...p, emailLogin: e.target.value }))}
                  fullWidth
                />
              </MDBox>

              <Divider sx={{ my: 2 }} />

              <MDTypography variant="button" color="text" fontWeight="medium">
                Rechnungsadresse
              </MDTypography>
              <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
                <TextField
                  label="Straße"
                  value={manageForm.street}
                  onChange={(e) => setManageForm((p) => ({ ...p, street: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="PLZ"
                  value={manageForm.zip}
                  onChange={(e) => setManageForm((p) => ({ ...p, zip: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Stadt"
                  value={manageForm.city}
                  onChange={(e) => setManageForm((p) => ({ ...p, city: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Land"
                  value={manageForm.country}
                  onChange={(e) => setManageForm((p) => ({ ...p, country: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="USt-IdNr."
                  value={manageForm.vatId}
                  onChange={(e) => setManageForm((p) => ({ ...p, vatId: e.target.value }))}
                  fullWidth
                />
              </MDBox>

              <Divider sx={{ my: 2 }} />

              <MDTypography variant="button" color="text" fontWeight="medium">
                Zahlungsdaten (Lastschrift)
              </MDTypography>
              <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
                <TextField label="Zahlungsart" value="Lastschrift" disabled fullWidth />
                <TextField
                  label="Kontoinhaber"
                  value={manageForm.accountHolder}
                  onChange={(e) => setManageForm((p) => ({ ...p, accountHolder: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="IBAN"
                  value={manageForm.iban}
                  onChange={(e) => setManageForm((p) => ({ ...p, iban: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="BIC"
                  value={manageForm.bic}
                  onChange={(e) => setManageForm((p) => ({ ...p, bic: e.target.value }))}
                  fullWidth
                />
              </MDBox>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <MDButton variant="text" color="error" onClick={submitDelete} disabled={deleteBusy || manageBusy}>
            {deleteBusy ? "Löscht…" : "Kunde löschen"}
          </MDButton>

          <MDBox display="flex" gap={1}>
            <MDButton variant="text" color="dark" onClick={closeManage} disabled={deleteBusy || manageBusy}>
              Schließen
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={submitUpdate}
              disabled={deleteBusy || manageBusy || !manageForm?.companyName?.trim()}
            >
              {manageBusy ? "Speichert…" : "Speichern"}
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
